import { downloadTextFile } from '@/lib/downloadFile';
import {
  decryptWithPassphrase,
  encryptWithPassphrase,
} from '@/lib/crypto/vault';
import type { AIProvider } from '@/contexts/AppContext';
import * as storage from './storage';
import { validateWorkspaceExport } from './validate';
import type {
  EncryptedWorkspaceExport,
  PortableAIProvider,
  WorkspaceExport,
  WorkspaceSettings,
} from './types';

const APP_CONFIG_LS_KEY = 'nostr:app-config';

/** Strip API keys — never leave the browser via export/sync. */
export function toPortableProviders(
  providers: AIProvider[] | undefined | null,
): PortableAIProvider[] {
  if (!providers?.length) return [];
  return providers.map((p) => ({
    id: p.id,
    name: p.name,
    baseUrl: p.baseUrl,
    useCorsProxy: p.useCorsProxy,
    models: (p.models ?? []).map((m) => ({
      id: m.id,
      name: m.name,
      providerId: m.providerId || p.id,
    })),
    modelsFetchedAt: p.modelsFetchedAt,
  }));
}

/**
 * Read public provider list from app-config localStorage (same origin).
 * Used when building export/sync payloads outside React.
 */
export function readPortableProvidersFromLocalStorage(): {
  providers: PortableAIProvider[];
  defaultAiProvider?: string;
} {
  try {
    const raw = localStorage.getItem(APP_CONFIG_LS_KEY);
    if (!raw) return { providers: [] };
    const parsed = JSON.parse(raw) as {
      aiProviders?: AIProvider[];
      defaultAiProvider?: string;
    };
    return {
      providers: toPortableProviders(parsed.aiProviders),
      defaultAiProvider: parsed.defaultAiProvider,
    };
  } catch {
    return { providers: [] };
  }
}

/**
 * Merge portable providers into app-config localStorage.
 * Never overwrites existing API keys for the same provider id.
 */
export function applyPortableProvidersToLocalStorage(
  providers: PortableAIProvider[] | undefined,
  defaultAiProvider?: string,
  mode: 'merge' | 'replace' = 'merge',
): void {
  if (!providers?.length && defaultAiProvider === undefined) return;
  try {
    const raw = localStorage.getItem(APP_CONFIG_LS_KEY);
    const existing = raw
      ? (JSON.parse(raw) as Partial<{
          aiProviders: AIProvider[];
          defaultAiProvider?: string;
        }>)
      : {};
    const current = existing.aiProviders ?? [];
    const byId = new Map(current.map((p) => [p.id, p]));

    let next: AIProvider[];
    if (mode === 'replace') {
      // Keep secrets for providers that still exist; drop the rest of local list
      next = (providers ?? []).map((p) => {
        const prev = byId.get(p.id);
        return {
          id: p.id,
          name: p.name,
          baseUrl: p.baseUrl,
          useCorsProxy: p.useCorsProxy,
          models: p.models ?? [],
          modelsFetchedAt: p.modelsFetchedAt,
          apiKey: prev?.apiKey ?? '',
        };
      });
    } else {
      next = [...current];
      for (const p of providers ?? []) {
        const idx = next.findIndex((x) => x.id === p.id);
        if (idx >= 0) {
          const prev = next[idx];
          next[idx] = {
            ...prev,
            name: p.name,
            baseUrl: p.baseUrl,
            useCorsProxy: p.useCorsProxy,
            models: p.models?.length ? p.models : prev.models,
            modelsFetchedAt: p.modelsFetchedAt ?? prev.modelsFetchedAt,
            // keep apiKey
          };
        } else {
          next.push({
            id: p.id,
            name: p.name,
            baseUrl: p.baseUrl,
            useCorsProxy: p.useCorsProxy,
            models: p.models ?? [],
            modelsFetchedAt: p.modelsFetchedAt,
            apiKey: '',
          });
        }
      }
    }

    const updated = {
      ...existing,
      aiProviders: next,
      ...(defaultAiProvider !== undefined
        ? { defaultAiProvider }
        : {}),
    };
    localStorage.setItem(APP_CONFIG_LS_KEY, JSON.stringify(updated));
    // Notify same-tab React state (native `storage` event is cross-tab only)
    window.dispatchEvent(
      new CustomEvent('opendork:localstorage', {
        detail: { key: APP_CONFIG_LS_KEY },
      }),
    );
    window.dispatchEvent(new Event('opendork:providers-restored'));
  } catch (err) {
    console.warn('Could not restore provider config from snapshot', err);
  }
}

export async function buildWorkspaceExport(options?: {
  projectIds?: string[];
  /** Omit message bodies (smaller / less sensitive sync). */
  excludeMessages?: boolean;
}): Promise<WorkspaceExport> {
  const settings = await storage.getSettings();
  let projects = await storage.getAllProjects();
  let chats = await storage.getAllChats();
  let messages = await storage.getAllMessages();

  if (options?.projectIds?.length) {
    const idSet = new Set(options.projectIds);
    projects = projects.filter((p) => idSet.has(p.id));
    chats = chats.filter((c) => c.projectId != null && idSet.has(c.projectId));
    const chatIds = new Set(chats.map((c) => c.id));
    messages = messages.filter((m) => chatIds.has(m.chatId));
  }

  const excludeMessages =
    options?.excludeMessages ?? settings.syncExcludeMessages ?? false;
  if (excludeMessages) {
    messages = [];
  }

  // Strip vault meta from export (not portable across machines without key wrap)
  const { vault: _v, ...settingsSafe } = settings;
  const { providers, defaultAiProvider } = readPortableProvidersFromLocalStorage();

  return {
    version: 1,
    exportedAt: Date.now(),
    app: 'opendork',
    format: 'plain',
    settings: {
      historyMode: settingsSafe.historyMode,
      encryptLocal: settingsSafe.encryptLocal,
      defaultModelId: settingsSafe.defaultModelId,
      globalInstructions: settingsSafe.globalInstructions ?? '',
      syncExcludeMessages: settingsSafe.syncExcludeMessages,
      idleLockMinutes: settingsSafe.idleLockMinutes,
    },
    projects,
    chats,
    messages,
    providers,
    defaultAiProvider,
  };
}

export async function exportWorkspaceToFile(options?: {
  projectIds?: string[];
  filename?: string;
}): Promise<void> {
  const data = await buildWorkspaceExport(options);
  const stamp = new Date().toISOString().slice(0, 10);
  const name =
    options?.filename ??
    (options?.projectIds?.length === 1
      ? `opendork-project-${stamp}.json`
      : `opendork-workspace-${stamp}.json`);
  await downloadTextFile(name, JSON.stringify(data, null, 2));
}

export async function exportEncryptedWorkspaceToFile(
  passphrase: string,
  options?: { projectIds?: string[]; filename?: string },
): Promise<void> {
  const plain = await buildWorkspaceExport(options);
  const sealed = await encryptWithPassphrase(JSON.stringify(plain), passphrase);
  const bundle: EncryptedWorkspaceExport = {
    version: 1,
    exportedAt: Date.now(),
    app: 'opendork',
    format: 'encrypted',
    salt: sealed.salt,
    iv: sealed.iv,
    ct: sealed.ct,
  };
  const stamp = new Date().toISOString().slice(0, 10);
  const name = options?.filename ?? `opendork-workspace-${stamp}.odk.json`;
  await downloadTextFile(name, JSON.stringify(bundle, null, 2));
}

export function isEncryptedExport(raw: unknown): raw is EncryptedWorkspaceExport {
  if (typeof raw !== 'object' || raw === null) return false;
  const o = raw as EncryptedWorkspaceExport;
  return (
    o.app === 'opendork' &&
    o.format === 'encrypted' &&
    typeof o.salt === 'string' &&
    typeof o.iv === 'string' &&
    typeof o.ct === 'string'
  );
}

export function parseWorkspaceExport(
  raw: unknown,
  options?: { jsonByteLength?: number },
): WorkspaceExport {
  return validateWorkspaceExport(raw, options);
}

export async function decryptExport(
  raw: unknown,
  passphrase: string,
): Promise<WorkspaceExport> {
  if (!isEncryptedExport(raw)) {
    return parseWorkspaceExport(raw);
  }
  const json = await decryptWithPassphrase(raw, passphrase);
  const byteLength = new TextEncoder().encode(json).length;
  return parseWorkspaceExport(JSON.parse(json), { jsonByteLength: byteLength });
}

export async function importWorkspaceFromJson(
  raw: unknown,
  mode: 'merge' | 'replace' = 'merge',
  passphrase?: string,
): Promise<{ projects: number; chats: number; messages: number; providers: number }> {
  let data: WorkspaceExport;
  if (isEncryptedExport(raw)) {
    if (!passphrase) {
      throw new Error('This backup is encrypted — enter the passphrase');
    }
    data = await decryptExport(raw, passphrase);
  } else {
    // Estimate size from re-serialized payload for limit checks
    const approx = (() => {
      try {
        return new TextEncoder().encode(JSON.stringify(raw)).length;
      } catch {
        return undefined;
      }
    })();
    data = parseWorkspaceExport(raw, { jsonByteLength: approx });
  }

  if (mode === 'replace') {
    const current = await storage.getSettings();
    const settings: WorkspaceSettings = {
      ...current,
      ...(data.settings ?? {}),
      vault: current.vault,
      activeChatByProject: {},
      activeProjectId: data.projects[0]?.id,
      // Preserve freestanding active chat when present
      activeChatId: data.chats[0]?.id ?? current.activeChatId,
    };
    await storage.replaceWorkspace({
      settings,
      projects: data.projects,
      chats: data.chats,
      messages: data.messages,
    });
    applyPortableProvidersToLocalStorage(
      data.providers,
      data.defaultAiProvider,
      'replace',
    );
  } else {
    const existingProjects = new Set((await storage.getAllProjects()).map((p) => p.id));
    const existingChats = new Set((await storage.getAllChats()).map((c) => c.id));
    const projectIdMap = new Map<string, string>();
    const chatIdMap = new Map<string, string>();

    const projects = data.projects.map((p) => {
      let id = p.id;
      if (existingProjects.has(id)) {
        id = crypto.randomUUID();
      }
      projectIdMap.set(p.id, id);
      return { ...p, id, updatedAt: Date.now() };
    });

    const chats = data.chats.map((c) => {
      let id = c.id;
      if (existingChats.has(id)) {
        id = crypto.randomUUID();
      }
      chatIdMap.set(c.id, id);
      const projectId =
        c.projectId == null || c.projectId === ''
          ? null
          : (projectIdMap.get(c.projectId) ?? c.projectId);
      return { ...c, id, projectId, updatedAt: Date.now() };
    });

    const messages = data.messages.map((m) => {
      const chatId = chatIdMap.get(m.chatId) ?? m.chatId;
      const id = crypto.randomUUID();
      return { ...m, id, chatId };
    });

    await storage.mergeWorkspace({ projects, chats, messages });

    // Bring in global instructions when local is empty and backup has them
    const incomingGlobal = data.settings?.globalInstructions?.trim();
    if (incomingGlobal) {
      const current = await storage.getSettings();
      if (!current.globalInstructions?.trim()) {
        await storage.saveSettings({
          ...current,
          globalInstructions: data.settings!.globalInstructions,
        });
      }
    }

    applyPortableProvidersToLocalStorage(
      data.providers,
      data.defaultAiProvider,
      'merge',
    );
  }

  return {
    projects: data.projects.length,
    chats: data.chats.length,
    messages: data.messages.length,
    providers: data.providers?.length ?? 0,
  };
}

export async function readImportFile(file: File): Promise<unknown> {
  const text = await file.text();
  return JSON.parse(text);
}
